import { BookingData } from "./cart";
import { IBookingAvailability } from "./component";
import { ICustomerDetail } from "./customer";
import { LoadingStage } from "./shared";
import { IDetailTour, IPriceGroup, ITourFare, ITourFareBookingTime } from "./tours";

export enum CheckoutType {
    CART = "cart",
    QUOTE = "quote",
    BOOK_NOW = "book-now",
}

export interface ICheckoutContext {
    checkoutType: CheckoutType;
    quoteId?: string;
}

export interface IServiceResult<T> {
    isSuccess: boolean;
    data?: T;
    errorMessage?: string;
}

// Convert namespace to module exports
export type IProductDetail = IDetailTour;
export type BookingPrice = ITourFare;
export type BookingPriceGroup = IPriceGroup;

export interface IProduct {
    rrp: string;
    name: string;
    extras: string;
    rating: number | null;
    address: {
        geometry: {
            lat: string;
            lng: string;
        };
        formattedAddress: string;
    };
    country: string;
    pickups: string;
    tourCode: string;
    weekDays: string[];
    favourite: boolean;
    freePhone: string;
    itinerary: string;
    noRefunds: number;
    productId: string;
    workPhone: string;
    bringItems: string[];
    categories: {
        countryNames: string[];
        states: string[];
        regions: string[];
        countries: string[];
        locations: string[];
        transport: string[];
        activities: string[];
        endLocation: string;
        countryCodes: string[];
        accommodation: string[];
        startLocation: string;
        transportName: string[];
        endLocationName: string;
        accommodationName: string[];
        startLocationName: string;
        endLocationGeocode: string;
        startLocationGeocode: string;
    };
    conditions: string;
    externalId: string | null;
    seatsCount: number | null;
    supplierid: string;
    isOverlayed: boolean;
    sleepsCount: number | null;
    tradingName: string;
    vehicleType: string[];
    durationDays: string;
    instructions: string;
    isNewHotDeal: boolean;
    luggageLimit: string;
    maxGroupSize: string;
    minGroupSize: string;
    productClass: string;
    supplierName: string;
    apiProviderId: number;
    durationNight: string;
    includedItems: string[];
    productTeaser: string;
    ageRestriction: string;
    departureDates: string[] | null;
    importantInfos: string;
    itineraryLunch: string[];
    itineraryMeals: string[];
    itineraryTitle: string[];
    productDetails: string;
    recurrenceDate: string;
    itineraryDinner: string[];
    noRefundMessage: string | null;
    providerDetails: string;
    operatingPartner: string;
    productImagePath: string;
    productOverlayId: string | null;
    reservationPhone: string;
    accommodationStyle: string[];
    cancellationPolicy: {
        [key: string]: {
            fareName: string[];
            usePercentMode: string;
            cancellationFeeRules: {
                feeAmount: string;
                daysBeforeTravelDate: string;
            }[];
            cancellationFeePolicyDescription: string;
        };
    };
    dietaryRequirement: string;
    fitnessRequirement: string;
    itineraryBreakfast: string[];
    isCachecowSupported: boolean;
    tourBuilderSettings: number;
    itineraryDescription: string[];
    maxAgeRecommendation: string;
    minAgeRecommendation: string;
    safePracticeMeasures: string;
    ticketEntryFieldName: string | null;
    mandatoryFieldCheckout: any[];
    ticketEntryFieldFormat: string;
    specialCircumstanceDate: string;
    nonEBookingNotifications: number;
    productLargeSizeImagePath: string;
    showSafePracticedBusiness: boolean;
    ticketEntryFieldDescription: string | null;
    groupFaresForAvailabilityCheck: boolean;
    productSmallToMediumSizeImagePath: string;
    voucherInformationContactDetailsBits: string;
    faresprices: ITourFare[];
}

export interface IProductSearchResponse {
    totalRecords: number;
    fetchedRecords: number;
    totalDisplayRecords: number;
    results: IProduct[];
}

export interface CheckProductAvailsDateRange {
    startDate: Date;
    numDays: number;
}

export interface CheckProductAvailsData extends CheckProductAvailsDateRange {
    productId: string;
    bookingPriceGroup: BookingPriceGroup;
    selectedBookingTimeId: string;
    apiProviderId: number;
    groupFaresForAvailabilityCheck: boolean;
    bookingTime?: string;
}

export interface ICartItemData {
    id: number;
    user_id: string;
    group_id?: string | null;
    tdms_product_id: number;
    product_price_details_id: number;
    created_at?: string;
    updated_at?: string;
    booking_date: string;
    start_date: string;
    days: number;
    selected_index: number;
    availability: IBookingAvailability;
    availability_last_updated_at: string;
    booking_details: ITourFareBookingTime;
    is_availability_latest: boolean;
    time_id?: string | null;
    booking_quantity: number;
    booking_data: BookingData[];
    product: {
        id: number;
        tdms_product_id: number;
        created_at: string;
        updated_at: string;
        version: number;
        tdms_product_last_update_date: string;
        counter: number;
        json: IProduct;
    };
    is_product_latest?: boolean;
    quote_id?: number;
}

export interface IPickupTimeOption {
    id: string;
    displayName: string;
    isDefault: boolean;
    downstreamTimeRef: string | null;
    numAvailable?: number;
}

export interface IBookingPickUpTime {
    displayName: string;
    options: IPickupTimeOption[];
}

export interface IPickUpLocationOption {
    id: string; // externalId
    displayName: string;
}

export interface IPickUpLocationOptionWithRef extends IPickUpLocationOption {
    upstreamTimeRef: string | null;
}

export interface IBookingPickUpLocation {
    displayName: string;
    upstreamTimeRef: string | null;
    options: IPickUpLocationOption[];
}

export interface IDropOffLocationOption {
    id: string;
    displayName: string;
}

export interface IDropOffLocationOptionWithRef extends IDropOffLocationOption {
    upstreamTimeRef: string | null;
}

export interface IBookingDropOffLocation {
    displayName: string;
    upstreamTimeRef: string | null;
    options: IDropOffLocationOption[];
}

export interface IBookingCheckInData {
    pickupTimes: IBookingPickUpTime[];
    pickupLocations: IBookingPickUpLocation[];
    dropoffLocations: IBookingDropOffLocation[];
}

export interface IBookingCheckInOptions extends IBookingCheckInData {
    fareTypeId: string;
    productPricesDetailsId: string;
}

export interface ICartItemsByGroup {
    group_id: string | null;
    items: ICartItemData[];
}

export interface ICartItemFareData {
    id: string;
    name: string;
    bookingSystem: string;
}

export interface ICartItemsByFare extends ICartItemFareData {
    itemsByGroup: ICartItemsByGroup[];
}

export interface ICartItemProductData {
    productId: string;
    productClass: string;
    name: string;
    productImagePath: string;
    supplierName?: string | null;
    startLocationName: string;
}

export interface ICartItemsByProduct extends ICartItemProductData {
    itemsByFare: ICartItemsByFare[];
}

export interface IUpdateCartItemData {
    cartItemId: number;
    quantity: number;
    bookingData: BookingData[];
}

export interface ITotalRRPInfo {
    totalRRP: number;
    hasMissingPrices: boolean;
}

export interface ITotalNetRateInfo {
    totalNetRate: number;
    hasMissingPrices: boolean;
}

export interface IPriceInfo {
    totalRRP: number;
    totalNetRate: number;
    commission: number;
    commissionPercent: number;
    totalPrice: number;
    hasMissingPrices: boolean;
}

export interface IBookingCart {
    cartItems: ICartItemData[];
    cartItemsLoadingStage: LoadingStage;
    commission: number;
    customers: ICustomerDetail[];
    customersLoadingStage: LoadingStage;
    date?: Date | string;
    id?: string;
    totalRRP: number;
    totalNet: number;
    totalPrice: number;
    isShare?: boolean;
    bookingReference?: string;
    commissionPercent: number;
    optionalFieldValidated: boolean;
    canCheckout: boolean;
    redirectUrl?: string;
    step: number;
    currentStep: number;
    activeDiscount?: IActiveDiscountDetail;
}

// discount
export interface IActiveDiscountDetail {
    discount: number;
}

export interface IDiscountDetail {
    applicableDiscount: number;
    pointsAvailable: number;
}

export interface IPointsVerifyResponse {
    pointsChanged: boolean;
    pointsAvailable: number;
}
